import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import { IChatMessageGroup } from "./ChatMessageGroup";
import { ChatMessage } from "./ChatMessage";
import {
    AvaFilters,
    NavigationActionData,
    hasNavigationConversationIds,
    kboFiltersToApplicationFilters,
} from "../AgentUtils";
import { ChatActionsMap } from "./ChatAction";
import SingleAgentChatStore from "../Stores/SingleAgentChatStore";

export type ChatSuggestAction = (
    store: SingleAgentChatStore,
    suggestion: ChatSuggestion,
) => void | Promise<void>;

export interface ChatSuggestion {
    content: string;
    llmInput?: string;
    permanent?: boolean;
    action: ChatSuggestAction;
    serverAction?: ChatSuggestionServerAction;
    navigationURL?: string;
    doNotSendToLlm?: boolean;
    newChatName?: string;
}

export type ChatSuggestionServerAction =
    | LLMInputServerAction
    | GetAdditionalInsightsFromConversationsServerAction
    | ForceToolCallServerAction;

export interface ServerAction {
    type: string;
}

export interface LLMInputServerAction extends ServerAction {
    type: "LLMInput";
    arguments: {
        input: string;
    };
}

export interface GetAdditionalInsightsFromConversationsServerAction
    extends ServerAction {
    type: "GetAdditionalInsightsFromConversations";
    arguments: {
        topic: string;
        conversationIds: string[];
        useConversationSummaries?: boolean;
    };
}

export interface ForceToolCallServerAction extends ServerAction {
    type: "ForceToolCall";
    arguments: {
        toolCall: ForceableToolCall;
        input?: string;
    };
}

export enum ForceableToolCall {
    Insights = 1,
    AnalyzeDataPoint,
    AnalyzeModelResults,
    AnalyzeConversation,
    AnalyzeAgentPerformance,
    FindSimilarConversations,
}

export type ChatServerSuggestionActionType = "navigate" | "build_montage";
export type ChatServerSuggestionNavigationActionDomain = "conversations";

interface ChatServerSuggestionActionBase {
    type: ChatServerSuggestionActionType;
    data?: unknown;
}

interface ChatServerSuggestionNavigationAction
    extends ChatServerSuggestionActionBase {
    type: "navigate";
    domain: ChatServerSuggestionNavigationActionDomain;
    data: unknown;
}

export interface BuildMontageFromClipsData {
    clipIds: string[];
    audioSoundClipsCount?: number;
    minClipDate: string;
    maxClipDate: string;
}

interface ChatServerSuggestionBuildMontageAction
    extends ChatServerSuggestionActionBase {
    type: "build_montage";
    data: BuildMontageFromClipsData;
}

export interface ChatServerSuggestionGetDeeperInsightsAction {
    type: "get_deeper_insights_on_conversations";
    data: {
        topic: string;
        conversationIds: string[];
        useConversationSummaries?: boolean;
    };
}

export interface ChatServerSuggestionFauxResponseAction {
    type: "faux_response";
    data: {
        response: string;
        actions?: ChatActionsMap;
    };
}

export interface ChatServerSuggestionForceAnalysisToolCallAction {
    type: "force_tool_call";
    data: {
        toolCall: ForceableToolCall;
        input?: string;
    };
}

export interface ChatServerSuggestionClearAnalysisStateAction {
    type: "clear_analysis_state";
}

export interface ChatServerSuggestionShowMoreSuggestionsAction {
    type: "show_more_suggestions";
    data: {
        suggestions: ChatServerSuggestion[];
    };
}

export interface ChatServerSuggestionGetDeeperInsightsActionData {
    topic: string;
    conversationIds: string[];
    useConversationSummaries?: boolean;
}

export type ChatServerSuggestionAction =
    | ChatServerSuggestionNavigationAction
    | ChatServerSuggestionBuildMontageAction
    | ChatServerSuggestionGetDeeperInsightsAction
    | ChatServerSuggestionShowMoreSuggestionsAction
    | ChatServerSuggestionFauxResponseAction
    | ChatServerSuggestionForceAnalysisToolCallAction
    | ChatServerSuggestionClearAnalysisStateAction;

/**
 * Represents a suggestion generated by the agent to take a specific, predefined action
 */
export interface ChatServerSuggestion {
    llmInput?: string;
    content: string;
    action?: ChatServerSuggestionAction;
    permanent: boolean;
}

export function fromServerSuggestion(
    serverSuggestion: ChatServerSuggestion,
    messageGroup: IChatMessageGroup,
): ChatSuggestion | undefined {
    const suggestion: ChatSuggestion = {
        content: serverSuggestion.content,
        permanent: serverSuggestion.permanent,
        action: defaultAction,
    };

    if (!serverSuggestion.action) return suggestion;

    switch (serverSuggestion.action.type) {
        case "clear_analysis_state":
            suggestion.action = async (store) => {
                await store.clearAgentAnalysisState();
                await store.simulateUserMessageAndStreamedResponse(
                    "I want to analyze another set of calls",
                    "Awesome, I'm ready when you are! How can I help?",
                );
            };
            break;
        case "force_tool_call":
            suggestion.serverAction = {
                type: "ForceToolCall",
                arguments: {
                    ...serverSuggestion.action.data,
                    input:
                        serverSuggestion.action.data.input ??
                        serverSuggestion.llmInput,
                },
            };
            break;
        case "faux_response":
            const responseData = serverSuggestion.action.data;
            suggestion.action = async (store) => {
                await store.simulateUserMessageAndStreamedResponse(
                    suggestion.content,
                    responseData.response,
                    { actions: responseData.actions },
                );
            };
            break;
        case "show_more_suggestions":
            const data = serverSuggestion.action.data;
            suggestion.action = (store) => {
                store.replaceServerSuggestions(data.suggestions);
            };
            break;
        case "get_deeper_insights_on_conversations":
            suggestion.serverAction = {
                type: "GetAdditionalInsightsFromConversations",
                arguments: {
                    topic: serverSuggestion.action.data.topic,
                    conversationIds:
                        serverSuggestion.action.data.conversationIds,
                    useConversationSummaries:
                        serverSuggestion.action.data.useConversationSummaries,
                },
            };
            break;
        case "navigate":
            const domain = serverSuggestion.action.domain;
            if (domain === "conversations") {
                const navData = serverSuggestion.action
                    .data as NavigationActionData;

                suggestion.action = (store) =>
                    conversationsNavigationAction(store, navData);

                suggestion.navigationURL = "/app/conversations";
            }
            break;
        case "build_montage":
            const clipData = serverSuggestion.action.data;
            if (clipData.audioSoundClipsCount === undefined) return;

            const agentMessage = messageGroup.messages.at(-1) as ChatMessage;
            if (clipData.audioSoundClipsCount === 0) {
                agentMessage.appendContent(
                    "\n\nPlease note: none of these clips are audio clips and cannot be included in a montage.",
                );
                return;
            } else if (
                clipData.audioSoundClipsCount < clipData.clipIds.length
            ) {
                agentMessage.appendContent(
                    "\n\nPlease note: some of these clips are not audio clips and will not be included in a montage.",
                );
            }

            suggestion.action = async (store, suggestion) => {
                await store.chat
                    .addUserMessageGroup()
                    .addMessage(suggestion.content)
                    .persist();

                await store.chat
                    .addAgentMessageGroup()
                    .addMessage("I'm taking you to montage builder now.")
                    .persist();

                await store.prepareBuildMontage(clipData);
            };
            suggestion.navigationURL = "/app/montagelibrary";
            break;
        default:
            suggestion.action = () => notImplementedAction(serverSuggestion);
    }

    return suggestion;
}

/**
 * Default behavior is to submit the server suggestions content as the user message
 * @param store
 * @param suggestion
 */
const defaultAction: ChatSuggestAction = (store, suggestion) => {
    store.streamingSubmitInput({
        manualInput: suggestion.content,
        suggestion: true,
    });
};

function notImplementedAction(serverSuggestion: ChatServerSuggestion) {
    console.warn(
        "Server suggestion action not implemented yet",
        serverSuggestion,
    );
}

async function conversationsNavigationAction(
    store: SingleAgentChatStore,
    data: NavigationActionData,
) {
    const rootStore = store.rootStore;
    const conversationsStore = rootStore.getStore(ConversationsStore);
    const applicationFiltersStore = rootStore.getStore(ApplicationFiltersStore);

    if (hasNavigationConversationIds(data)) {
        conversationsStore.setConversationIds(data.conversationIds);
    } else {
        conversationsStore.setConversationIds(undefined);
        const properFilters = await kboFiltersToApplicationFilters(
            data as AvaFilters,
        );
        applicationFiltersStore.applyExternalFilters(properFilters, true);
        applicationFiltersStore.saveCurrentFilterState("Conversations");
    }
    conversationsStore.setPageNumberAndGetConversations(1);
}
